import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import apiService from '../../utils/apiService';

// Fetch last 3 trades for a bot
export const fetchBotTrades = createAsyncThunk(
    'botTrades/fetchBotTrades',
    async (botId, { rejectWithValue }) => {
        try {
            const response = await apiService.getWithAuth(
                `/bots/${botId}/trades-last3`,
                { maxRetries: 4 },
            );

            return {
                botId,
                success: true,
                trades: response.data?.trades || [],
                count: response.data?.count || 0,
                message: response.data?.message || 'Trades fetched successfully',
                tokenAddress: response.data?.tokenAddress
            };
        }
        catch (error) {
            return rejectWithValue({
                botId,
                message: error.message
            });
        }
    },
    {
        condition: (botId, { getState }) => {
            const entry = getState().botTrades.botTrades[botId];
            return entry?.loading !== 'pending';
        },
    },
);

const initialState = {
    botTrades: {}, // Store trades per bot ID
    loading: 'idle',
    error: null,
    message: null
};

const botTradesSlice = createSlice({
    name: 'botTrades',
    initialState,
    reducers: {
        resetBotTrades(state) {
            state.botTrades = {};
            state.loading = 'idle';
            state.error = null;
            state.message = null;
        },
        clearError(state) {
            state.error = null;
        },
        clearMessage(state) {
            state.message = null;
        },
        clearBotTrades(state, action) {
            const botId = action.payload;
            if (state.botTrades[botId]) {
                delete state.botTrades[botId];
            }
        },
        /** Combo: seed trades from cached dashboard snapshot (no trades-last3). */
        seedBotTrades(state, action) {
            const { botId: rawId, trades, tokenAddress } = action.payload || {};
            const botId =
                rawId !== undefined && rawId !== null ? String(rawId) : "";
            if (!botId || !Array.isArray(trades)) return;
            state.botTrades[botId] = {
                trades,
                loading: "idle",
                error: null,
                message: null,
                count: trades.length,
                tokenAddress: tokenAddress ?? state.botTrades[botId]?.tokenAddress,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch bot trades
            .addCase(fetchBotTrades.pending, (state, action) => {
                const botId = action.meta.arg;
                state.loading = 'pending';
                state.error = null;
                state.message = null;
                
                // Initialize bot trades if not exists
                if (!state.botTrades[botId]) {
                    state.botTrades[botId] = {
                        trades: [],
                        loading: 'pending',
                        error: null,
                        message: null,
                        count: 0
                    };
                } else {
                    state.botTrades[botId].loading = 'pending';
                    state.botTrades[botId].error = null;
                }
            })
            .addCase(fetchBotTrades.fulfilled, (state, action) => {
                const { botId, trades, count, message, tokenAddress } = action.payload;
                state.loading = 'idle';
                state.error = null;
                
                if (!state.botTrades[botId]) {
                    state.botTrades[botId] = {};
                }
                
                state.botTrades[botId] = {
                    trades,
                    loading: 'idle',
                    error: null,
                    message,
                    count,
                    tokenAddress
                };
            })
            .addCase(fetchBotTrades.rejected, (state, action) => {
                const { botId, message } = action.payload || { botId: action.meta.arg, message: action.error.message };
                state.loading = 'idle';
                state.error = message;
                
                if (!state.botTrades[botId]) {
                    state.botTrades[botId] = {};
                }
                
                state.botTrades[botId] = {
                    trades: [],
                    loading: 'idle',
                    error: message,
                    message: null,
                    count: 0
                };
            });
    }
});

export const { resetBotTrades, clearError, clearMessage, clearBotTrades, seedBotTrades } = botTradesSlice.actions;

// Selectors
export const selectBotTrades = createSelector(
  [state => state.botTrades.botTrades, (state, botId) => botId],
  (botTrades, botId) => botTrades[botId] || { trades: [], loading: 'idle', error: null, count: 0 }
);
export const selectBotTradesLoading = (state) => state.botTrades.loading;
export const selectBotTradesError = (state) => state.botTrades.error;

export default botTradesSlice.reducer; 